import React, {useState, useCallback, useEffect} from 'react';

import {PageWrapperLayout, MainHeader, MainFooter, StepProgressBar} from 'presentation/components';
import {Box, Stack, Typography} from '@mui/material';
import {ChangeDialogsGroup} from 'presentation/pages/private/booking/components';
import {FlowEnum} from 'presentation/pages/private/booking/constants';
import {translations} from 'presentation/translations-new';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {AuthorizationsEnum, FeaturesEnum, HomeEnum} from 'presentation/constants';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {CustomStepper} from 'presentation/components/CustomStepper';
import {useIsComingFromInitialFlowStore} from 'presentation/pages/others/selection-flow/store/useIsComingFromInitialFlowStore';
import Tutorial from 'presentation/components/Tutorial';
import {Placement} from 'react-joyride';
import {RequestAttachment} from './components/RequestAttachment';
import {useRequestAuthorization} from '../../hooks';
import {DiscardChangesModal} from '../../components';
import {StyledFooterContainer} from '../../styles';
import {ProviderSelection} from './components/ProviderSelection';
import {STEPS_LABELS, RequestAuthorizationStep, stepLabels} from './constants';
import {CurrentLocation} from '../../components/CurrentLocation';
import {useAuthorizationsStore} from '../../store/authorizationsStore';
import {Location} from '../../components/Location';
import {useRequestAuthStepSync} from './hooks/useRequestAuthStepSync';
import {useUploadedFilesStore} from 'presentation/components/UploadFiles/store/useUploadedFilesStore';

const {pageTitle} = translations['pt-br'].privateFlow.requestAuthorizations.request.addAttachments;
const {tourDescription} =
	translations['pt-br'].privateFlow.requestAuthorizations.request.ProviderSelection;

export function RequestAuthorization() {
	const navigate = useNavigate();
	const {state: navState} = useLocation();
	const forceLocationStep = (navState as {forceLocationStep?: boolean} | null)?.forceLocationStep;
	const {isMobile} = useMediaQueries();

	const {isEnabled: isAutomationFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_AUTOMACAO_AUTOSOL,
	);

	const isBusinessHours = useAuthorizationsStore(state => state.businessHours);
	const {resetProvidersList, setProviderObservation, setNuControle} = useAuthorizationsStore();
	const isComingFromInitialFlow = useIsComingFromInitialFlowStore(
		currentState => currentState.isComingFromInitialFlow,
	);
	const resetUploadedFilesState = useUploadedFilesStore(state => state.resetUploadedFiles);

	const {
		exitFlowModalState,
		setExitFlowModalState,
		handleOpenExitModal,
		handleConfirmExitFlow,
		cdUfSelected,
		nmCidadeSelected,
	} = useRequestAuthorization();

	const getInitialStep = (): RequestAuthorizationStep => {
		if (forceLocationStep) {
			return RequestAuthorizationStep.LOCATION;
		}

		const shouldGoToLocationStep =
			(!isAutomationFlagEnabled && isComingFromInitialFlow) ||
			!cdUfSelected ||
			!nmCidadeSelected;

		if (shouldGoToLocationStep) {
			return RequestAuthorizationStep.LOCATION;
		}

		if (!isAutomationFlagEnabled) return RequestAuthorizationStep.ATTACHMENTS;

		return RequestAuthorizationStep.PROVIDER_SELECTION;
	};

	const [currentStepState, setCurrentStepState] =
		useState<RequestAuthorizationStep>(getInitialStep());

	useRequestAuthStepSync({currentStep: currentStepState});

	const handleGoToRequestAttachment = useCallback(() => {
		setCurrentStepState(RequestAuthorizationStep.ATTACHMENTS);
		document.getElementById('request-auth-content')?.scrollIntoView({behavior: 'smooth'});
	}, []);

	const handleBackToStart = useCallback(() => {
		resetProvidersList();
		setProviderObservation('');
		setNuControle('');
		resetUploadedFilesState();
		navigate(HomeEnum.HOME_PAGE, {replace: true});
	}, [
		navigate,
		resetProvidersList,
		resetUploadedFilesState,
		setNuControle,
		setProviderObservation,
	]);

	const handleBack = useCallback(() => {
		if (currentStepState === RequestAuthorizationStep.ATTACHMENTS) {
			handleOpenExitModal();
			return;
		}

		if (currentStepState === RequestAuthorizationStep.LOCATION) {
			navigate(AuthorizationsEnum.AUTHORIZATIONS);
			return;
		}

		if (isComingFromInitialFlow) {
			navigate(AuthorizationsEnum.AUTHORIZATIONS);
			return;
		}

		setCurrentStepState(RequestAuthorizationStep.LOCATION);
	}, [currentStepState, handleOpenExitModal, isComingFromInitialFlow, navigate]);

	const goToLocationStep = () => {
		setCurrentStepState(RequestAuthorizationStep.LOCATION);
	};

	useEffect(() => {
		if (!isBusinessHours) {
			navigate(AuthorizationsEnum.AUTHORIZATIONS);
		}
	}, [isBusinessHours]);

	useEffect(
		() => () => {
			resetProvidersList();
			setProviderObservation('');
			resetUploadedFilesState();
		},
		[],
	);

	const shouldShowStepLabel =
		(isMobile && currentStepState) ||
		(!isMobile && currentStepState === RequestAuthorizationStep.LOCATION);

	return (
		<PageWrapperLayout title={pageTitle}>
			<div id="request-auth-content">
				<MainHeader
					title={pageTitle}
					hideBackBtn={
						isMobile && currentStepState === RequestAuthorizationStep.ATTACHMENTS
					}
					handleBack={handleBack}
					dataTagId={{
						closeButton: AutoSolEnum.CLICK_FECHAR_AUTOSOL_SAUDE,
					}}
					hideClose={currentStepState === RequestAuthorizationStep.PROVIDER_SELECTION}
					handleClose={handleOpenExitModal}
					variant="subRoute">
					<Stack
						gap={isMobile ? 1 : 3}
						px={isMobile ? 2 : 3}
						pb={isMobile ? '12px' : '20px'}>
						<Box maxWidth={647}>
							{isMobile && currentStepState !== RequestAuthorizationStep.LOCATION ? (
								<StepProgressBar
									mt={1}
									currentStep={
										stepLabels.indexOf(STEPS_LABELS[currentStepState]) + 1
									}
									totalSteps={stepLabels.length}
								/>
							) : null}
							{!isMobile && currentStepState !== RequestAuthorizationStep.LOCATION ? (
								<CustomStepper
									activeStep={stepLabels.indexOf(STEPS_LABELS[currentStepState])}
									stepLabels={stepLabels}
								/>
							) : null}

							{shouldShowStepLabel ? (
								<Typography variant="body1" fontSize="1.125rem">
									{STEPS_LABELS[currentStepState] || null}
								</Typography>
							) : null}
						</Box>
					</Stack>
				</MainHeader>

				{currentStepState === RequestAuthorizationStep.PROVIDER_SELECTION ? (
					<CurrentLocation
						shouldNavigateTo={goToLocationStep}
						dataTagId={AutoSolEnum.CLICK_ALTERAR_LOCALIZACAO_AUTOSOL}
					/>
				) : null}

				{currentStepState === RequestAuthorizationStep.LOCATION ? <Location /> : null}

				{currentStepState === RequestAuthorizationStep.PROVIDER_SELECTION ? (
					<ProviderSelection onNextStep={handleGoToRequestAttachment} />
				) : null}

				{currentStepState === RequestAuthorizationStep.ATTACHMENTS ? (
					<RequestAttachment onBackToStart={handleBackToStart} />
				) : null}
			</div>

			<ChangeDialogsGroup flowType={FlowEnum.AUTHORIZATIONS} />
			<DiscardChangesModal
				open={exitFlowModalState}
				setOpen={setExitFlowModalState}
				onConfirm={handleConfirmExitFlow}
			/>

			<StyledFooterContainer
				$mb={currentStepState !== RequestAuthorizationStep.LOCATION ? 72 : 0}>
				<MainFooter />
			</StyledFooterContainer>
			{currentStepState === RequestAuthorizationStep.PROVIDER_SELECTION ? (
				<Tutorial
					steps={[
						{
							target: '#header',
							content: tourDescription,
							placement: 'bottom' as Placement,
							disableBeacon: true,
						},
					]}
					storageKey="authRequestFirstAccess"
				/>
			) : null}
		</PageWrapperLayout>
	);
}
